




































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Validator, FieldError } from 'vee-validate';

import AddressComponent from '@/shared/components/Address/Address.vue';
import Button from '@/shared/components/Button/Button.vue';
import ButtonBack from '@/shared/components/Button/ButtonBack.vue';
import Checkbox from '@/shared/components/Inputs/Checkbox.vue';
import EssencialInformation from './components/EssencialInformation.vue';
import Heading from '@/shared/components/Heading/Heading.vue';
import TermsDesktopModal from '@/shared/components/Terms/TermsDesktopModal.vue';
import PolicyDesktopModal from '@/shared/components/Terms/PolicyDesktopModal.vue';
import ReCAPTCHABranding from '@/shared/components/ReCAPTCHABranding/ReCAPTCHABranding.vue';

import Address from '@/classes/Address';
import Customer from '@/classes/Customer';

import { ICustomer } from '@/interfaces/Customer';
import { IZipCodeNotFound } from '@/interfaces/ZipCodeNotFound';
import { ITraceableError } from '@/interfaces/Error';

import Cookie from '@/helpers/cookie';
import { getValueIfIsNotNil } from '@/helpers/values';
import { EPhoneKind, EErrorScope, EErrorExhibition } from '@/helpers/enums';
import { redirectToPolicy } from '@/helpers/redirects';
import { executeCaptcha } from '@/helpers/captcha';

const ErrorModule = namespace('App/Error');
const CustomerModule = namespace('App/Customer');
const BehaviorModule = namespace('App/Behavior');

@Component({
  components: {
    Address: AddressComponent,
    ButtonBack,
    Button,
    Checkbox,
    EssencialInformation,
    Heading,
    TermsDesktopModal,
    PolicyDesktopModal,
    ReCAPTCHABranding,
  }
})
export default class Register extends Vue {
  private firstEmailVerification: boolean = true;
  private validator = new Validator();

  private form = {
    address: new Address({
      street_name: '',
      street_number: '',
      city: '',
      neighborhood: '',
      state: '',
      postal_code: '',
      ibge_code: ''
    }),
    essencialInformation: {
      name: '',
      email: '',
      document: '',
      password: '',
      phone: ''
    },
    acceptTerms: null,
    errors: this.validator.errors,
    states: {
      termsAndConditions: false
    }
  };

  public $refs!: {
    essentials: Vue;
    address: Vue;
  };

  private registerError = '';

  @Prop({ default: false })
  private readonly goToDashboard: boolean;

  @CustomerModule.State('isLoading')
  private isLoading: boolean;

  @CustomerModule.Action('signup')
  private signup!: ({ customer, isEverywhere }: { customer: ICustomer, isEverywhere: boolean })=> void;

  @CustomerModule.Action('notFoundZipCode')
  private notFoundZipCode: (newCustomer: IZipCodeNotFound)=> void;

  @ErrorModule.Getter('errorsByExhibitionAndScope')
  private errorsByExhibitionAndScope: Function;

  @ErrorModule.Mutation('hideErrorsByScope')
  private hideErrorsByScope: Function;

  @BehaviorModule.Getter
  private isCaptchaValid: boolean;

  @BehaviorModule.Action
  private setCaptchaValid: Function;

  private clearErrors() {
    return this.hideErrorsByScope(EErrorScope.register);
  }

  @Watch('form.essencialInformation.email')
  emailChanged() {
    this.firstEmailVerification = true;
  }

  private get customer(): ICustomer {
    return new Customer({
      name: this.form.essencialInformation.name,
      email: this.form.essencialInformation.email,
      document: this.form.essencialInformation.document.replace(/\D/g, ''),
      password: this.form.essencialInformation.password,
      terms_optin: this.form.acceptTerms,
      addresses: [this.form.address],
      phone_numbers: [
        {
          kind: EPhoneKind.mobile,
          phone: `+55${this.form.essencialInformation.phone.replace(/\D/g, '')}`,
          is_default: true,
          is_active: true
        }
      ]
    });
  }

  private get allowedCookies(): boolean {
    const cookie = new Cookie({ name: 'COOKIE_EMP_ALLOW_COOKIES' });
    return cookie.value === 'allow';
  }

  private get isMobile () {
    return this.$vuetify.breakpoint.smAndDown;
  }

  private goToLogin() {
    this.clearErrors();

    this.$router.push({
      name: !this.goToDashboard ? 'checkout' : 'loginToDashboard',
      query: this.$router.currentRoute.query
    });
  }

  private zipCodeNotFound(): void {
    const { name, email, phone_numbers } = this.customer;
    const phone = phone_numbers && phone_numbers[0].phone;
    const { postal_code } = this.form.address;
    const newCustomer: IZipCodeNotFound = {
      name,
      email,
      phone: getValueIfIsNotNil(phone),
      zipCode: postal_code
    };
    this.form.address = {
      ...this.form.address,
      street_name: '',
      street_number: '',
      city: '',
      neighborhood: '',
      state: '',
      ibge_code: ''
    };
    // this.notFoundZipCode(newCustomer);
  }

  private validateForm() {
    if (!this.firstEmailVerification) {
      this.validator.fields.items = this.validator.fields.items.filter(
        item => !item.rules.verifyEmail
      );
    }

    return new Promise(async (resolve, reject) => {
      let error = { erroredElementHeight: null as any };

      const result = await this.validator.validateAll({
        ...this.form.address,
        ...this.form.essencialInformation,
      });

      if (!this.form.address.zipCodeData) {
        if (this.form.address.postal_code.length >= 8) {
          this.validator.validate('postal_code', '0');
        } else {
          this.validator.validate('postal_code', this.form.address.postal_code.replace(/\D/g, ''));
        }
      }

      const essentials = this.$refs.essentials;
      const address = this.$refs.address;
      let element = null;
      this.$nextTick(() => {
        const firstField = Object.keys(this.validator.errors.collect())[0];
        if (firstField) {
          if (essentials.$refs.hasOwnProperty(firstField)) {
            element = essentials.$refs[firstField] as any;
            error.erroredElementHeight =
              element.$el.offsetParent.offsetTop - element.$el.offsetHeight;
            element.focus();
          } else if (address.$refs.hasOwnProperty(firstField)) {
            element = address.$refs[firstField] as any;
            error.erroredElementHeight =
              element.$el.offsetParent.offsetTop - element.$el.offsetHeight;
            element.focus();
          }
        }
      });

      result ? resolve() : reject(error);
    });
  }

  private async submitForm() {
    this.registerError = '';

    try {
      await this.validateForm();
      await this.signup({
        customer: this.customer,
        isEverywhere: false,
      });
    } catch (error) {
      if (error.response) {
        const { status, message, cause } = error.response.data;
        const isInvalidNumber = cause && cause.includes('telefone');

        if (isInvalidNumber) {
          this.validator.errors.add({
            field: 'phone',
            msg: 'Preencha com um celular válido',
          });
        }

        if (status >= 400 && message) {
          this.registerError = message;
        } else {
          this.registerError = this.$t('App.Checkout.Register.error') as string;
        }
      }

      this.firstEmailVerification = false;
      if(error.erroredElementHeight){
        window.scrollTo(0, error.erroredElementHeight);
      }
    }
  }

  private mounted() {
    this.clearErrors();

    this.validator.attach({
      name: 'email',
      rules: 'verifyEmail'
    });

    executeCaptcha('register')
      .then((success: boolean) => {
        this.setCaptchaValid(success);
      });
  }

  private destroyed() {
    this.validator.reset();
  }
}
